import { useState } from "react";
import axios from "axios";
import { CheckCircle2, KeyRound, Mail, X } from "lucide-react";
import { API } from "../lib/apiClient";

export function ChangePasswordModal({ onClose }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (newPassword.length < 6) return setError("Yeni şifre en az 6 karakter olmalı");
    if (newPassword !== confirmPassword) return setError("Yeni şifreler eşleşmiyor");
    if (newPassword === currentPassword) return setError("Yeni şifre mevcut şifreyle aynı olamaz");
    setBusy(true);
    try {
      await axios.post(`${API}/auth/change-password`, { currentPassword, newPassword });
      setSuccess(true);
      setTimeout(() => onClose(), 1500);
    } catch (err) {
      setError(err?.response?.data?.detail || "Şifre değiştirilemedi");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" data-testid="change-password-modal">
      <div className="modal" style={{ maxWidth: 480 }}>
        <div className="modal-head">
          <h2><KeyRound size={18} style={{ marginRight: 8, verticalAlign: -3 }} />Şifre değiştir</h2>
          <button className="icon-button" data-testid="change-password-close" onClick={onClose}><X size={18} /></button>
        </div>
        {success ? (
          <div className="password-success" data-testid="password-success">
            <CheckCircle2 size={28} />
            <strong>Şifreniz başarıyla değiştirildi</strong>
            <span>Bir sonraki girişte yeni şifrenizi kullanın.</span>
          </div>
        ) : (
          <form onSubmit={submit}>
            <div className="form-grid">
              <label className="field full">
                <span>Mevcut şifre</span>
                <div className="password-input">
                  <input type={showCurrent ? "text" : "password"} data-testid="current-password-input" required value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} placeholder="Şu anki şifreniz" />
                  <button type="button" className="pwd-toggle" onClick={() => setShowCurrent((v) => !v)} tabIndex={-1}>{showCurrent ? "Gizle" : "Göster"}</button>
                </div>
              </label>
              <label className="field full">
                <span>Yeni şifre</span>
                <div className="password-input">
                  <input type={showNew ? "text" : "password"} data-testid="new-password-input" required minLength={6} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="En az 6 karakter" />
                  <button type="button" className="pwd-toggle" onClick={() => setShowNew((v) => !v)} tabIndex={-1}>{showNew ? "Gizle" : "Göster"}</button>
                </div>
              </label>
              <label className="field full">
                <span>Yeni şifre (tekrar)</span>
                <input type={showNew ? "text" : "password"} data-testid="confirm-password-input" required value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Yeni şifreyi tekrar yazın" />
              </label>
            </div>
            {error && <div className="auth-error" data-testid="password-error"><X size={14} />{error}</div>}
            <div className="modal-actions">
              <button type="button" className="outline-button" onClick={onClose}>Vazgeç</button>
              <button type="submit" className="primary-button" data-testid="change-password-submit" disabled={busy}>
                <KeyRound size={15} /> {busy ? "Değiştiriliyor…" : "Şifreyi değiştir"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export function ChangeEmailModal({ currentEmail, onClose, onSuccess }) {
  const [newEmail, setNewEmail] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const trimmed = newEmail.trim().toLowerCase();
    if (!trimmed || !trimmed.includes("@") || !trimmed.split("@")[1]?.includes(".")) return setError("Geçerli bir e-posta girin");
    if (trimmed === (currentEmail || "").trim().toLowerCase()) return setError("Yeni e-posta mevcut ile aynı olamaz");
    if (!currentPassword) return setError("Mevcut şifrenizi girin");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/auth/change-email`, { newEmail: trimmed, currentPassword });
      setSuccess(true);
      if (onSuccess) onSuccess(data?.email || trimmed);
      setTimeout(() => onClose(), 1800);
    } catch (err) {
      setError(err?.response?.data?.detail || "E-posta değiştirilemedi");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" data-testid="change-email-modal">
      <div className="modal" style={{ maxWidth: 480 }}>
        <div className="modal-head">
          <h2><Mail size={18} style={{ marginRight: 8, verticalAlign: -3 }} />E-posta değiştir</h2>
          <button className="icon-button" data-testid="change-email-close" onClick={onClose}><X size={18} /></button>
        </div>
        {success ? (
          <div className="password-success" data-testid="email-success">
            <CheckCircle2 size={28} />
            <strong>E-posta başarıyla güncellendi</strong>
            <span>Sonraki giriş için yeni e-postanızı kullanın.</span>
          </div>
        ) : (
          <form onSubmit={submit}>
            <div className="form-grid">
              <label className="field full">
                <span>Mevcut e-posta</span>
                <input type="email" value={currentEmail || ""} disabled />
              </label>
              <label className="field full">
                <span>Yeni e-posta</span>
                <input type="email" data-testid="new-email-input" required value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="ornek@mail.com" autoComplete="off" />
              </label>
              <label className="field full">
                <span>Mevcut şifre (doğrulama için)</span>
                <div className="password-input">
                  <input type={showPwd ? "text" : "password"} data-testid="current-password-email-input" required value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} placeholder="Şu anki şifreniz" autoComplete="current-password" />
                  <button type="button" className="pwd-toggle" onClick={() => setShowPwd((v) => !v)} tabIndex={-1}>{showPwd ? "Gizle" : "Göster"}</button>
                </div>
              </label>
            </div>
            {error && <div className="auth-error" data-testid="email-error"><X size={14} />{error}</div>}
            <div className="modal-actions">
              <button type="button" className="outline-button" onClick={onClose}>Vazgeç</button>
              <button type="submit" className="primary-button" data-testid="change-email-submit" disabled={busy}>
                <Mail size={15} /> {busy ? "Güncelleniyor…" : "E-postayı değiştir"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
